import type { User } from "firebase/auth";
import { updateProfile } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
  type DocumentData,
  type Query,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { defaultPermissionsByCompanyRole } from "../domain/permissions";
import type { CompanyAccess, CompanyRole, GlobalRole, PermissionSet, UserProfile } from "../types/imobi";
import { db, functions, storage } from "./firebase";

export type UserAccessProfile = {
  globalRole: GlobalRole;
  companyRole?: CompanyRole;
  accessibleCompanyIds: string[];
  manageableCompanyIds: string[];
  auditorCompanyIds: string[];
  companyRoles: Record<string, CompanyRole>;
  companyPermissions: Record<string, PermissionSet>;
};

export type ManagedUser = UserProfile & {
  companyAccess: CompanyAccess[];
};

export type UserCompanyAssignmentInput = {
  companyId: string;
  companyRole: CompanyRole;
  permissions: PermissionSet;
  status: "ativo" | "suspenso";
};

export type SaveUserInput = {
  id?: string;
  cpf: string;
  cpfRaw: string;
  fullName: string;
  birthDate: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  globalRole: GlobalRole;
  status: UserProfile["status"];
  assignments: UserCompanyAssignmentInput[];
};

export type SelfRegistrationInput = {
  cpf: string;
  cpfRaw: string;
  fullName: string;
  birthDate: string;
  email: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

export type UpdateSelfAccountInput = {
  fullName: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
};

type UsersSubscriptionScopeKey = {
  globalRole: GlobalRole;
  manageableCompanyIds: string[];
};

const defaultUserAccessProfile: UserAccessProfile = {
  globalRole: "USER",
  accessibleCompanyIds: [],
  manageableCompanyIds: [],
  auditorCompanyIds: [],
  companyRoles: {},
  companyPermissions: {}
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function mapUser(id: string, data: Record<string, unknown>): UserProfile {
  const displayName = String(data.fullName ?? data.displayName ?? data.email ?? "");

  return {
    id,
    cpf: data.cpf ? String(data.cpf) : undefined,
    cpfRaw: data.cpfRaw ? String(data.cpfRaw) : undefined,
    displayName,
    fullName: data.fullName ? String(data.fullName) : displayName,
    birthDate: data.birthDate ? String(data.birthDate) : undefined,
    email: String(data.email ?? ""),
    globalRole: (data.globalRole ?? "USER") as GlobalRole,
    status: (data.status ?? "ativo") as UserProfile["status"],
    phone: data.phone ? String(data.phone) : undefined,
    address: data.address ? String(data.address) : undefined,
    city: data.city ? String(data.city) : undefined,
    state: data.state ? String(data.state) : undefined,
    zipCode: data.zipCode ? String(data.zipCode) : undefined,
    photoUrl: data.photoUrl ? String(data.photoUrl) : undefined,
    companyIds: Array.isArray(data.companyIds) ? data.companyIds.map(String) : [],
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    createdByName: data.createdByName ? String(data.createdByName) : undefined,
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined,
    updatedByName: data.updatedByName ? String(data.updatedByName) : undefined
  };
}

function mapCompanyAccess(id: string, data: Record<string, unknown>): CompanyAccess {
  const companyRole = (data.companyRole ?? "CONSULTA") as CompanyRole;
  const storedPermissions = (data.permissions ?? {}) as Partial<PermissionSet>;

  return {
    id,
    userId: String(data.userId ?? ""),
    companyId: String(data.companyId ?? ""),
    companyRole,
    permissions: {
      ...defaultPermissionsByCompanyRole[companyRole],
      ...storedPermissions
    },
    auditorCompanyId: data.auditorCompanyId ? String(data.auditorCompanyId) : undefined,
    status: (data.status ?? "ativo") as CompanyAccess["status"],
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function chunk<T>(items: T[], size: number): T[][] {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
}

function mergeManagedUsers(users: UserProfile[], accessRecords: CompanyAccess[]): ManagedUser[] {
  return users.map((profile) => ({
    ...profile,
    companyAccess: accessRecords.filter((access) => access.userId === profile.id && access.status === "ativo")
  }));
}

async function fetchUsersByIds(userIds: string[]): Promise<UserProfile[]> {
  if (!db || !userIds.length) {
    return [];
  }

  const firestore = db;
  const uniqueUserIds = Array.from(new Set(userIds));
  const snapshots = await Promise.all(
    chunk(uniqueUserIds, 10).map((ids) =>
      getDocs(query(collection(firestore, "users"), where(documentId(), "in", ids)))
    )
  );

  return snapshots.flatMap((snapshot) => snapshot.docs.map((userDoc) => mapUser(userDoc.id, userDoc.data())));
}

function subscribeMany<T>(
  queries: Query<DocumentData>[],
  mapSnapshot: (id: string, data: Record<string, unknown>) => T,
  onChange: (items: T[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!queries.length) {
    onChange([]);
    return () => undefined;
  }

  const buckets = new Map<number, T[]>();
  const unsubscribes = queries.map((itemsQuery, index) =>
    onSnapshot(
      itemsQuery,
      (snapshot) => {
        buckets.set(index, snapshot.docs.map((snapshotDoc) => mapSnapshot(snapshotDoc.id, snapshotDoc.data())));
        onChange(Array.from(buckets.values()).flat());
      },
      (error) => onError(error.message)
    )
  );

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export async function ensureCurrentUserProfile(user: User): Promise<void> {
  if (!db) {
    return;
  }

  if (functions) {
    try {
      const callable = httpsCallable(functions, "syncCurrentUserProfile");
      await callable();
      return;
    } catch (error) {
      console.warn("Sincronizacao do cadastro via Function falhou; tentando fallback pelo cliente.", error);
    }
  }

  const firestore = db;
  const userLabel = user.displayName ?? user.email ?? user.uid;
  const userRef = doc(firestore, "users", user.uid);
  const snapshot = await getDoc(userRef);

  if (snapshot.exists()) {
    const companyIds = Array.isArray(snapshot.data().companyIds) ? snapshot.data().companyIds : [];
    await setDoc(userRef, {
      displayName: snapshot.data().displayName ?? userLabel,
      email: user.email ?? snapshot.data().email ?? "",
      photoUrl: user.photoURL ?? snapshot.data().photoUrl ?? null,
      companyCount: companyIds.length,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid,
      updatedByName: userLabel
    }, { merge: true });
    return;
  }

  if (user.email) {
    const pendingSnapshot = await getDocs(query(collection(firestore, "users"), where("email", "==", user.email.toLocaleLowerCase("pt-BR")), limit(1)));
    const pendingDoc = pendingSnapshot.docs.find((profileDoc) => profileDoc.id !== user.uid);

    if (pendingDoc) {
      const pendingAccessSnapshot = await getDocs(query(collection(firestore, "companyAccess"), where("userId", "==", pendingDoc.id)));
      const batch = writeBatch(firestore);
      const pendingData = pendingDoc.data();

      batch.set(userRef, {
        ...pendingData,
        displayName: pendingData.displayName ?? userLabel,
        email: user.email,
        photoUrl: user.photoURL ?? pendingData.photoUrl ?? null,
        companyCount: Array.isArray(pendingData.companyIds) ? pendingData.companyIds.length : 0,
        updatedAt: serverTimestamp(),
        updatedBy: user.uid,
        updatedByName: userLabel
      }, { merge: true });

      pendingAccessSnapshot.docs.forEach((accessDoc) => {
        const accessData = accessDoc.data();
        batch.set(doc(firestore, "companyAccess", `${user.uid}_${accessData.companyId}`), {
          ...accessData,
          userId: user.uid,
          updatedAt: serverTimestamp(),
          updatedBy: user.uid
        }, { merge: true });
      });

      await batch.commit();
      return;
    }
  }

  await setDoc(userRef, {
    displayName: userLabel,
    fullName: userLabel.toLocaleUpperCase("pt-BR"),
    email: user.email ?? "",
    globalRole: "USER",
    status: "ativo",
    photoUrl: user.photoURL ?? null,
    companyIds: [],
    companyCount: 0,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    createdByName: userLabel,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: userLabel
  }, { merge: true });
}

export async function createSelfUserProfile(user: User, input: SelfRegistrationInput): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await setDoc(doc(db, "users", user.uid), {
    cpf: input.cpf,
    cpfRaw: input.cpfRaw,
    displayName: input.fullName,
    fullName: input.fullName,
    birthDate: input.birthDate,
    email: input.email.toLocaleLowerCase("pt-BR"),
    phone: input.phone ?? "",
    address: input.address ?? "",
    city: input.city ?? "",
    state: input.state ?? "",
    zipCode: input.zipCode ?? "",
    globalRole: "USER",
    status: "ativo",
    photoUrl: user.photoURL ?? null,
    companyIds: [],
    companyCount: 0,
    createdAt: serverTimestamp(),
    createdBy: user.uid,
    createdByName: input.fullName,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: input.fullName
  }, { merge: true });
}

export async function updateSelfUserAccount(user: User, input: UpdateSelfAccountInput): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const fullName = input.fullName.trim().toLocaleUpperCase("pt-BR");
  if (!fullName) {
    throw new Error("Informe seu nome completo.");
  }

  await setDoc(doc(db, "users", user.uid), {
    displayName: fullName,
    fullName,
    phone: input.phone ?? "",
    address: input.address ?? "",
    city: input.city ?? "",
    state: input.state ?? "",
    zipCode: input.zipCode ?? "",
    email: user.email ?? "",
    updatedAt: serverTimestamp(),
    updatedBy: user.uid,
    updatedByName: fullName
  }, { merge: true });
}

export async function updateSelfUserPhoto(user: User, file: File | null): Promise<string | null> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  let photoUrl: string | null = null;
  if (file) {
    if (!storage) {
      throw new Error("Storage nao configurado.");
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const photoRef = ref(storage, `users/${user.uid}/profile/photo-${Date.now()}.${extension}`);
    await uploadBytes(photoRef, file, { contentType: file.type || "image/jpeg" });
    photoUrl = await getDownloadURL(photoRef);
  }

  await setDoc(doc(db, "users", user.uid), {
    photoUrl,
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  }, { merge: true });
  await updateProfile(user, { photoURL: photoUrl });
  return photoUrl;
}

export function subscribeCurrentUserProfile(userId: string, onChange: (profile: UserProfile | null) => void, onError: (message: string) => void): Unsubscribe {
  if (!db) {
    onChange(null);
    return () => undefined;
  }

  return onSnapshot(
    doc(db, "users", userId),
    (snapshot) => {
      onChange(snapshot.exists() ? mapUser(snapshot.id, snapshot.data()) : null);
    },
    (error) => onError(error.message)
  );
}

export function subscribeUserAccessProfile(
  userId: string,
  onChange: (profile: UserAccessProfile) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db) {
    onChange(defaultUserAccessProfile);
    return () => undefined;
  }

  let globalRole: GlobalRole = "USER";
  let accessRecords: CompanyAccess[] = [];

  const emit = () => {
    const activeRecords = accessRecords.filter((access) => access.status === "ativo");
    const manageableCompanyIds = activeRecords
      .filter((access) => access.permissions.canManageCompanyUsers)
      .map((access) => access.companyId);
    const auditorCompanyIds = activeRecords
      .filter((access) => access.companyRole === "AUDITOR")
      .map((access) => access.companyId);

    onChange({
      globalRole,
      companyRole: activeRecords[0]?.companyRole,
      accessibleCompanyIds: activeRecords.map((access) => access.companyId),
      manageableCompanyIds,
      auditorCompanyIds,
      companyRoles: Object.fromEntries(activeRecords.map((access) => [access.companyId, access.companyRole])),
      companyPermissions: Object.fromEntries(activeRecords.map((access) => [access.companyId, access.permissions]))
    });
  };

  const unsubscribeProfile = onSnapshot(
    doc(db, "users", userId),
    (snapshot) => {
      const data = snapshot.data();
      globalRole = (data?.globalRole ?? "USER") as GlobalRole;
      emit();
    },
    (error) => onError(error.message)
  );

  const accessQuery = query(collection(db, "companyAccess"), where("userId", "==", userId));
  const unsubscribeAccess = onSnapshot(
    accessQuery,
    (snapshot) => {
      accessRecords = snapshot.docs.map((accessDoc) => mapCompanyAccess(accessDoc.id, accessDoc.data()));
      emit();
    },
    (error) => onError(error.message)
  );

  return () => {
    unsubscribeProfile();
    unsubscribeAccess();
  };
}

export function subscribeManagedUsers(
  accessProfile: UsersSubscriptionScopeKey,
  onChange: (users: ManagedUser[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db) {
    onChange([]);
    return () => undefined;
  }

  const firestore = db;
  const isAdminScope = accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN";
  const managerCompanyIds = accessProfile.manageableCompanyIds.slice(0, 10);

  if (!isAdminScope && managerCompanyIds.length === 0) {
    onChange([]);
    return () => undefined;
  }

  let profiles: UserProfile[] = [];
  let accessRecords: CompanyAccess[] = [];
  let fetchGeneration = 0;

  const emit = () => onChange(mergeManagedUsers(profiles, accessRecords));

  if (isAdminScope) {
    const unsubscribeUsers = onSnapshot(
      query(collection(firestore, "users"), orderBy("fullName")),
      (snapshot) => {
        profiles = snapshot.docs.map((userDoc) => mapUser(userDoc.id, userDoc.data()));
        emit();
      },
      (error) => onError(error.message)
    );
    const unsubscribeAccess = onSnapshot(
      query(collection(firestore, "companyAccess")),
      (snapshot) => {
        accessRecords = snapshot.docs.map((accessDoc) => mapCompanyAccess(accessDoc.id, accessDoc.data()));
        emit();
      },
      (error) => onError(error.message)
    );

    return () => {
      unsubscribeUsers();
      unsubscribeAccess();
    };
  }

  return subscribeMany(
    chunk(managerCompanyIds, 10).map((companyIds) => query(collection(firestore, "companyAccess"), where("companyId", "in", companyIds))),
    mapCompanyAccess,
    (records) => {
      const generation = fetchGeneration + 1;
      fetchGeneration = generation;
      accessRecords = records;

      void fetchUsersByIds(records.map((record) => record.userId))
        .then((fetchedProfiles) => {
          if (generation !== fetchGeneration) {
            return;
          }

          profiles = fetchedProfiles;
          emit();
        })
        .catch((error) => onError(error instanceof Error ? error.message : "Nao foi possivel carregar usuarios vinculados."));
    },
    onError
  );
}

export function subscribeCompanyUsers(
  companyId: string,
  onChange: (users: UserProfile[]) => void,
  onError: (message: string) => void
): Unsubscribe {
  if (!db || !companyId) {
    onChange([]);
    return () => undefined;
  }

  return onSnapshot(
    query(collection(db, "users"), where("companyIds", "array-contains", companyId)),
    (snapshot) => onChange(snapshot.docs.map((userDoc) => mapUser(userDoc.id, userDoc.data())).sort((left, right) => (left.fullName ?? left.displayName).localeCompare(right.fullName ?? right.displayName))),
    (error) => onError(error.message)
  );
}

export async function findUnassignedUsers(searchText: string): Promise<ManagedUser[]> {
  if (!db) {
    return [];
  }

  const firestore = db;
  const normalized = searchText.trim().toLocaleUpperCase("pt-BR");
  const snapshot = await getDocs(query(collection(firestore, "users"), where("companyCount", "==", 0), limit(25)));
  const users = snapshot.docs
    .map((userDoc) => mapUser(userDoc.id, userDoc.data()))
    .filter((profile) => {
      const searchable = [profile.fullName, profile.displayName, profile.email, profile.cpf].join(" ").toLocaleUpperCase("pt-BR");
      return !normalized || searchable.includes(normalized);
    });

  return mergeManagedUsers(users, []);
}

export async function saveUserProfile(input: SaveUserInput, currentUser: User, currentAccessProfile: UserAccessProfile, previousAssignments: CompanyAccess[] = []): Promise<string> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const firestore = db;
  const isMaster = currentAccessProfile.globalRole === "MASTER";
  const isAdmin = currentAccessProfile.globalRole === "ADMIN";
  const manageableCompanyIds = new Set(currentAccessProfile.manageableCompanyIds);
  const userId = input.id ?? `user-${input.cpfRaw || Date.now()}`;
  const activeAssignments = input.assignments.filter((assignment) => assignment.status === "ativo");
  const companyIds = activeAssignments.map((assignment) => assignment.companyId);
  const userLabel = currentUser.displayName ?? currentUser.email ?? currentUser.uid;

  if (!isMaster && input.globalRole === "MASTER") {
    throw new Error("Somente MASTER pode atribuir nivel MASTER.");
  }

  if (!isMaster && !isAdmin && activeAssignments.some((assignment) => !manageableCompanyIds.has(assignment.companyId))) {
    throw new Error("Gerente so pode atribuir empresas que ele mesmo gerencia.");
  }

  const batch = writeBatch(firestore);
  const userRef = doc(firestore, "users", userId);

  batch.set(userRef, {
    cpf: input.cpf,
    cpfRaw: input.cpfRaw,
    displayName: input.fullName,
    fullName: input.fullName,
    birthDate: input.birthDate,
    email: input.email.toLocaleLowerCase("pt-BR"),
    phone: input.phone ?? "",
    address: input.address ?? "",
    city: input.city ?? "",
    state: input.state ?? "",
    zipCode: input.zipCode ?? "",
    globalRole: isMaster ? input.globalRole : "USER",
    status: input.status,
    companyIds,
    companyCount: companyIds.length,
    updatedAt: serverTimestamp(),
    updatedBy: currentUser.uid,
    updatedByName: userLabel,
    ...(input.id ? {} : {
      createdAt: serverTimestamp(),
      createdBy: currentUser.uid,
      createdByName: userLabel
    })
  }, { merge: true });

  activeAssignments.forEach((assignment) => {
    const accessRef = doc(firestore, "companyAccess", `${userId}_${assignment.companyId}`);
    batch.set(accessRef, {
      userId,
      companyId: assignment.companyId,
      companyRole: assignment.companyRole,
      permissions: assignment.permissions,
      status: "ativo",
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid,
      ...(previousAssignments.some((previous) => previous.companyId === assignment.companyId) ? {} : {
        createdAt: serverTimestamp(),
        createdBy: currentUser.uid
      })
    }, { merge: true });
  });

  previousAssignments
    .filter((previous) => !companyIds.includes(previous.companyId))
    .forEach((previous) => {
      const accessRef = doc(firestore, "companyAccess", `${userId}_${previous.companyId}`);
      batch.set(accessRef, {
        status: "suspenso",
        updatedAt: serverTimestamp(),
        updatedBy: currentUser.uid
      }, { merge: true });
    });

  await batch.commit();
  return userId;
}

export function permissionsForRole(role: CompanyRole): PermissionSet {
  return { ...defaultPermissionsByCompanyRole[role] };
}
